import { useEffect, useState } from "react";
import { api, CHART, money } from "@/lib/api";
import { Card, Overline, KpiCard } from "@/components/common";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { TriangleAlert } from "lucide-react";

const axis = { stroke: "#A1A1AA", fontSize: 11, tickLine: false, axisLine: false };
const tip = { contentStyle: { borderRadius: 12, border: "1px solid #E5E7EB", fontSize: 12, fontFamily: "Manrope" } };

function ChartCard({ title, children, className, sub }) {
  return (
    <Card className={`p-6 ${className || ""}`}>
      <div className="mb-4"><Overline>{title}</Overline>{sub && <p className="text-xs text-[#A1A1AA] mt-1">{sub}</p>}</div>
      <div className="h-56"><ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer></div>
    </Card>
  );
}

export default function Revenue() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/revenue").then(({ data }) => setD(data)); }, []);
  if (!d) return <div className="h-96 animate-pulse bg-white border border-[#E5E7EB] rounded-xl" />;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-[#1C1F1E]">Revenue</h1>
        <p className="text-[#52525B] mt-1">Every dollar across every department.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {d.kpis.map((k) => <KpiCard key={k.label} {...k} testid={`rev-kpi-${k.label.toLowerCase().replace(/[^a-z]/g, "-")}`} />)}
      </div>

      {/* Revenue Leakage — prominent */}
      <Card className="p-0 overflow-hidden border-[#DC2626]/25">
        <div className="px-6 py-4 bg-[#DC2626]/[0.04] border-b border-[#DC2626]/15 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2.5">
            <TriangleAlert size={18} className="text-[#DC2626]" />
            <span className="font-display text-lg font-semibold text-[#1C1F1E]">Revenue Leakage</span>
          </div>
          <div className="text-right">
            <div className="overline !text-[#DC2626]">Total Identified Monthly Opportunity</div>
            <div className="font-display text-3xl font-bold text-[#DC2626] font-mono2" data-testid="leakage-total">{d.leakage.total}</div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-[#E5E7EB]">
          {d.leakage.items.map((it) => (
            <div key={it.label} className="p-5" data-testid={`leakage-${it.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              <div className="text-sm text-[#52525B] leading-snug h-10">{it.label}</div>
              <div className="font-display text-2xl font-semibold text-[#DC2626] font-mono2 mt-2">{it.value}</div>
              <div className="text-xs text-[#A1A1AA] mt-0.5 capitalize">{it.note}</div>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ChartCard title="30-Day Revenue" className="lg:col-span-2">
          <AreaChart data={d.thirty_day} margin={{ left: -14, right: 8, top: 8 }}>
            <defs><linearGradient id="r30" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0F3F2D" stopOpacity={0.2} /><stop offset="100%" stopColor="#0F3F2D" stopOpacity={0} /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEF1EF" vertical={false} />
            <XAxis dataKey="day" {...axis} interval={4} />
            <YAxis {...axis} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
            <Tooltip {...tip} formatter={(v) => [money(v), "Revenue"]} />
            <Area type="monotone" dataKey="revenue" stroke="#0F3F2D" strokeWidth={2} fill="url(#r30)" />
          </AreaChart>
        </ChartCard>

        <Card className="p-6">
          <Overline>Revenue by Category</Overline>
          <div className="flex items-center gap-6 mt-4">
            <div className="h-48 w-48 shrink-0">
              <ResponsiveContainer><PieChart>
                <Pie data={d.categories} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78} paddingAngle={2} strokeWidth={0}>
                  {d.categories.map((e, i) => <Cell key={i} fill={CHART[i % CHART.length]} />)}
                </Pie>
                <Tooltip {...tip} formatter={(v, n) => [money(v), n]} />
              </PieChart></ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-2">
              {d.categories.map((c, i) => (
                <div key={c.name} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-[#52525B]"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: CHART[i % CHART.length] }} />{c.name}</span>
                  <span className="font-mono2 text-[#1C1F1E]">{c.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <ChartCard title="Revenue by Hour">
          <BarChart data={d.revenue_by_hour} margin={{ left: -14, right: 8, top: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEF1EF" vertical={false} />
            <XAxis dataKey="hour" {...axis} /><YAxis {...axis} tickFormatter={(v) => `$${v / 1000}k`} />
            <Tooltip {...tip} formatter={(v) => [money(v), "Revenue"]} cursor={{ fill: "#0F3F2D08" }} />
            <Bar dataKey="revenue" fill="#2F7A57" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ChartCard>

        <ChartCard title="Revenue per Round" sub="Last 14 days">
          <LineChart data={d.rev_per_round} margin={{ left: -14, right: 8, top: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEF1EF" vertical={false} />
            <XAxis dataKey="day" {...axis} interval={2} /><YAxis {...axis} tickFormatter={(v) => `$${v}`} />
            <Tooltip {...tip} formatter={(v) => [`$${v}`, "Rev/Round"]} />
            <Line type="monotone" dataKey="value" stroke="#C7A24A" strokeWidth={2.5} dot={false} />
          </LineChart>
        </ChartCard>

        <ChartCard title="Utilization vs Revenue" className="lg:col-span-2" sub="Each point is an hour of the day">
          <ScatterChart margin={{ left: -14, right: 8, top: 8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEF1EF" />
            <XAxis type="number" dataKey="utilization" name="Utilization" unit="%" {...axis} />
            <YAxis type="number" dataKey="revenue" name="Revenue" {...axis} tickFormatter={(v) => `$${v / 1000}k`} />
            <Tooltip {...tip} cursor={{ strokeDasharray: "3 3" }} formatter={(v, n) => n === "Revenue" ? [money(v), n] : [`${v}%`, n]} />
            <Scatter data={d.util_vs_revenue} fill="#0F3F2D" />
          </ScatterChart>
        </ChartCard>
      </div>
    </div>
  );
}
